import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CardActions,
  CircularProgress,
  Chip,
  Divider,
  Pagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DashboardLayout from "../../layout/DashboardLayout";
import { fetchProducts, requestProductDemo, requestProduct } from "../../api/auth";
import { brand } from "../../theme";
import { toast } from "react-toastify";
import { EmptyState } from "../../components/EmptyState";

const CARD_ACCENTS = [
  { bg: "#FBF3D9", icon: brand.goldDark, border: brand.goldLight },
  { bg: "#f0fdf4", icon: "#16a34a", border: "#bbf7d0" },
  { bg: "#fdf4ff", icon: "#9333ea", border: "#e9d5ff" },
  { bg: "#fff7ed", icon: "#ea580c", border: "#fed7aa" },
  { bg: brand.terracottaTint, icon: brand.terracotta, border: brand.terracottaLight },
  { bg: "#fefce8", icon: "#ca8a04", border: "#fef08a" },
];

const PRODUCT_ICONS = [
  <AutoAwesomeIcon />,
  <RocketLaunchIcon />,
  <CalendarMonthIcon />,
  <AppsIcon />,
];

export default function PostBookingToolset() {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [requestingDemoId, setRequestingDemoId] = React.useState(null);
  const [buyDialogProduct, setBuyDialogProduct] = React.useState(null);
  const [buyQuantity, setBuyQuantity] = React.useState(1);
  const [buying, setBuying] = React.useState(false);

  const handleRequestDemo = async (product) => {
    setRequestingDemoId(product._id);
    try {
      await requestProductDemo(product._id);
      toast.success(`Demo request sent for ${product.title}.`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to request demo. Please try again.");
    } finally {
      setRequestingDemoId(null);
    }
  };

  const openBuyDialog = (product) => {
    setBuyDialogProduct(product);
    setBuyQuantity(1);
  };

  const handleConfirmBuy = async () => {
    if (!buyDialogProduct) return;
    setBuying(true);
    try {
      await requestProduct(buyDialogProduct._id, buyQuantity);
      toast.success(`Product request sent for ${buyDialogProduct.title}.`);
      setBuyDialogProduct(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to request product. Please try again.");
    } finally {
      setBuying(false);
    }
  };

  React.useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await fetchProducts();
        setProducts(res.data);
      } catch (err) {
        console.error("Failed to load products", err);
        setError("Failed to load products. Please refresh the page and try again.");
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  return (
    <DashboardLayout>
      <Box>
        {/* Page header */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1.5} mb={0.75}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: brand.terracotta,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <AppsIcon sx={{ fontSize: "1.1rem" }} />
            </Box>
            <Typography variant="h5" color="#0f172a">
              Post-Booking Toolset
            </Typography>
          </Box>
          <Typography variant="body2" color="#64748b" ml={0.25}>
            Explore powerful tools designed to automate alerts, manage ticketing
            processes, and improve operational efficiency.
          </Typography>
        </Box>

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {/* Empty state */}
        {!loading && !error && products.length === 0 && (
          <EmptyState
            icon={<AutoAwesomeIcon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }} />}
            title="No products available yet"
            description="Products will appear here once they've been published by the Helpdesk team."
          />
        )}

        {/* Product grid */}
        {!loading && !error && products.length > 0 && (
          <>
          <Grid container spacing={3}>
            {products.slice((page - 1) * 6, page * 6).map((product, i) => {
              const accent = CARD_ACCENTS[i % CARD_ACCENTS.length];
              const icon = PRODUCT_ICONS[i % PRODUCT_ICONS.length];
              return (
                <Grid key={product._id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ p: 3, flex: 1 }}>
                      {/* Icon + Available chip */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            bgcolor: accent.bg,
                            border: `1px solid ${accent.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: accent.icon,
                            "& svg": { fontSize: "1.3rem" },
                          }}
                        >
                          {icon}
                        </Box>
                        <Chip
                          label="Available"
                          size="small"
                          sx={{
                            bgcolor: "#dcfce7",
                            color: "#15803d",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>

                      <Typography variant="h6" color="#0f172a" gutterBottom>
                        {product.title}
                      </Typography>
                      <Typography variant="body2" color="#64748b" sx={{ lineHeight: 1.6 }}>
                        {product.description}
                      </Typography>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ px: 3, py: 2, gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ flex: 1 }}
                        onClick={() => openBuyDialog(product)}
                      >
                        Buy Product
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ flex: 1 }}
                        startIcon={<CalendarMonthIcon sx={{ fontSize: "0.95rem !important" }} />}
                        onClick={() => handleRequestDemo(product)}
                        disabled={requestingDemoId === product._id}
                      >
                        {requestingDemoId === product._id ? "Requesting…" : "Request Demo"}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {Math.ceil(products.length / 6) > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(products.length / 6)}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
          </>
        )}
      </Box>

      <Dialog open={!!buyDialogProduct} onClose={() => setBuyDialogProduct(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Request {buyDialogProduct?.title}</DialogTitle>
        <DialogContent>
          <TextField
            label="Quantity"
            type="number"
            value={buyQuantity}
            onChange={(e) => setBuyQuantity(Math.max(1, Number(e.target.value) || 1))}
            fullWidth
            sx={{ mt: 1 }}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuyDialogProduct(null)} disabled={buying}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmBuy} disabled={buying}>
            {buying ? "Sending…" : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
